import { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
//@ts-ignore
import { db } from "../../utils/firebase";

type Slot = {
    start: string;
    end: string;
};

type DayConfig = {
    enabled: boolean;
    slots: Slot[];
};

const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
];

interface Props {
    onClose: () => void;
    userId: string;
}

export default function WorkingHoursDrawer({
    onClose,
    userId,
}: Props) {
    const [workingHours, setWorkingHours] = useState<
        Record<string, DayConfig>
    >({});
    const [loading, setLoading] = useState(false);

    // ---------------------------
    // Default
    // ---------------------------
    const getDefaultHours = () => {
        const obj: Record<string, DayConfig> = {};
        days.forEach((day) => {
            obj[day] = {
                enabled: true,
                slots: [{ start: "09:00", end: "22:00" }],
            };
        });
        return obj;
    };

    // ---------------------------
    // Fetch On Mount
    // ---------------------------
    useEffect(() => {
        const fetchData = async () => {
            const snap = await getDoc(doc(db, "userDetails", userId));

            if (snap.exists()) {
                const data = snap.data();
                setWorkingHours(
                    data?.businessRules?.workingHours || getDefaultHours()
                );
            } else {
                setWorkingHours(getDefaultHours());
            }
        };

        fetchData();
    }, [userId]);

    // ---------------------------
    // Handlers
    // ---------------------------
    const toggleDay = (day: string) => {
        const updated = { ...workingHours };
        updated[day].enabled = !updated[day].enabled;
        setWorkingHours(updated);
    };

    const updateSlot = (
        day: string,
        index: number,
        field: "start" | "end",
        value: string
    ) => {
        const updated = { ...workingHours };
        updated[day].slots[index][field] = value;
        setWorkingHours(updated);
    };

    const addSlot = (day: string) => {
        const updated = { ...workingHours };
        if (updated[day].slots.length < 2) {
            updated[day].slots.push({
                start: "17:00",
                end: "22:00",
            });
            setWorkingHours(updated);
        }
    };

    const removeSlot = (day: string, index: number) => {
        const updated = { ...workingHours };
        updated[day].slots.splice(index, 1);
        setWorkingHours(updated);
    };

    // ---------------------------
    // Validation
    // ---------------------------
    const isValidDay = (dayConfig: DayConfig) => {
        if (!dayConfig.enabled) return true;

        for (let slot of dayConfig.slots) {
            if (!slot.start || !slot.end) return false;
            if (slot.start >= slot.end) return false;
        }

        if (dayConfig.slots.length === 2) {
            const [s1, s2] = dayConfig.slots;
            if (s1.end > s2.start) return false;
        }

        return true;
    };

    const [showSuccess, setShowSuccess] = useState(false);

    const handleSave = async () => {
        for (let day of days) {
            if (!isValidDay(workingHours[day])) {
                alert(`Invalid time range on ${day}`);
                return;
            }
        }

        try {
            setLoading(true);

            await updateDoc(doc(db, "userDetails", userId), {
                "businessRules.workingHours": {
                    timezone: "Asia/Kolkata",
                    ...workingHours,
                },
            });

            // Show success toast
            setShowSuccess(true);

            // Auto hide after 2.5s
            setTimeout(() => {
                setShowSuccess(false);
                onClose();
            }, 2500);

        } catch (error) {
            console.error(error);
            alert("Error saving working hours");
        } finally {
            setLoading(false);
        }
    };



    // ---------------------------
    // UI
    // ---------------------------
    return (
        <div className="fixed inset-0 z-50 flex">
            {/* Overlay */}
            <div
                className="flex-1 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div className="w-[480px] bg-white h-full shadow-2xl flex flex-col animate-slideIn">

                {/* Header */}
                <div className="px-6 py-5 border-b bg-gradient-to-r from-[#f4f9ed] to-white">
                    <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
                        Working Hours
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Set your business operating hours (IST)
                    </p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                    {days.map((day) => (
                        <div
                            key={day}
                            className="rounded-2xl border border-gray-200 p-5 bg-white shadow-sm hover:shadow-md transition"
                        >
                            {/* Day Header */}
                            <div className="flex justify-between items-center mb-4">
                                <span className="capitalize text-lg font-semibold text-gray-800">
                                    {day}
                                </span>

                                {/* Zafflo Toggle */}
                                <button
                                    onClick={() => toggleDay(day)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${workingHours[day]?.enabled
                                        ? "bg-[#6cbb23]"
                                        : "bg-gray-300"
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${workingHours[day]?.enabled
                                            ? "translate-x-6"
                                            : "translate-x-1"
                                            }`}
                                    />
                                </button>
                            </div>

                            {/* Time Slots */}
                            {workingHours[day]?.enabled && (
                                <div className="space-y-3">
                                    {workingHours[day]?.slots.map((slot, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3"
                                        >
                                            <input
                                                type="time"
                                                value={slot.start}
                                                onChange={(e) =>
                                                    updateSlot(day, index, "start", e.target.value)
                                                }
                                                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#6cbb23] focus:border-[#6cbb23] transition"
                                            />

                                            <span className="text-gray-400">—</span>

                                            <input
                                                type="time"
                                                value={slot.end}
                                                onChange={(e) =>
                                                    updateSlot(day, index, "end", e.target.value)
                                                }
                                                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#6cbb23] focus:border-[#6cbb23] transition"
                                            />

                                            {index === 1 && (
                                                <button
                                                    onClick={() => removeSlot(day, index)}
                                                    className="text-gray-400 hover:text-red-500 transition text-sm"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    {workingHours[day]?.slots.length < 2 && (
                                        <button
                                            onClick={() => addSlot(day)}
                                            className="text-sm font-medium text-[#6cbb23] hover:opacity-80 transition"
                                        >
                                            + Add Another Time Range
                                        </button>
                                    )}
                                </div>
                            )}

                            {!workingHours[day]?.enabled && (
                                <div className="text-sm text-gray-400 italic">
                                    Closed for this day
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Fixed Bottom Save Section */}
                <div className="px-6 py-4 border-t bg-white shadow-inner">
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="w-full bg-[#6cbb23] text-white py-3 rounded-2xl font-semibold text-sm tracking-wide hover:opacity-90 transition active:scale-[0.98]"
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>
            {showSuccess && (
                <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] animate-fadeIn">
                    <div className="bg-[#6cbb23] text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3">
                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            ✓
                        </div>
                        <span className="text-sm font-medium">
                            Working hours saved successfully
                        </span>
                    </div>
                </div>
            )}

        </div>
    );
}